export enum UserRole {
  ADMIN = 'ADMIN',
  CLIENT = 'CLIENT',
  INFLUENCER = 'INFLUENCER',
  AGENT = 'AGENT',
}

export enum SubmissionStatus {
  DRAFT = 'DRAFT',
  PENDING_REVIEW = 'PENDING_REVIEW',
  PENDING_SS_REVIEW = 'PENDING_SS_REVIEW',
  WITH_CLIENT = 'WITH_CLIENT',
  APPROVED = 'APPROVED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
  CLIENT_FEEDBACK = 'CLIENT_FEEDBACK',
  CLIENT_APPROVED_WITH_CHANGES = 'CLIENT_APPROVED_WITH_CHANGES',
  REJECTED = 'REJECTED',
  // Phase 5: admin-gated terminal state (creator/client-facing label "Closed after review").
  ABANDONED = 'ABANDONED',
}

export enum ContentType {
  REEL = 'REEL',
  STORY = 'STORY',
  POST = 'POST',
  TIKTOK = 'TIKTOK',
  YOUTUBE = 'YOUTUBE',
}

export enum Platform {
  INSTAGRAM = 'INSTAGRAM',
  TIKTOK = 'TIKTOK',
  YOUTUBE = 'YOUTUBE',
}

export enum CampaignStatus {
  DRAFT = 'DRAFT',
  ACTIVE = 'ACTIVE',
  PAUSED = 'PAUSED',
  COMPLETED = 'COMPLETED',
}

/**
 * Sold content-approval tier on a CampaignGroup (micro-campaign roadmap Stage 1).
 * Database column is nullable — null resolves to FULL (legacy default).
 */
export enum ApprovalTier {
  NO_APPROVAL = 'NO_APPROVAL',
  LEGAL_ONLY = 'LEGAL_ONLY',
  ONE_ROUND = 'ONE_ROUND',
  FULL = 'FULL',
}

/**
 * Sign-off state of one sampled post in the NO_APPROVAL spot-check queue
 * (PR-D, roadmap 1.7). PASSED/FLAGGED is server-derived from the 3-item
 * checklist: all true → PASSED, otherwise FLAGGED.
 */
export enum SpotCheckStatus {
  PENDING = 'PENDING',
  PASSED = 'PASSED',
  FLAGGED = 'FLAGGED',
}

/**
 * Optional sold upgrades on a CampaignGroup (PR-B, roadmap 1.2). Sold-scope
 * flags only — their workflows are not built (roadmap Open Question 6).
 */
export enum CampaignUpgrade {
  HIGH_LEVEL_RECRUITMENT = 'HIGH_LEVEL_RECRUITMENT',
  ADDITIONAL_MODERATION = 'ADDITIONAL_MODERATION',
  THIRD_PARTY_REVIEWS = 'THIRD_PARTY_REVIEWS',
  VIDEO_TESTIMONIALS = 'VIDEO_TESTIMONIALS',
  LIVE_FEED = 'LIVE_FEED',
  WEBINAR_EVENT = 'WEBINAR_EVENT',
  HYPE_CARD = 'HYPE_CARD',
}

export enum NotificationType {
  SUBMISSION_STATUS = 'SUBMISSION_STATUS',
  FEEDBACK = 'FEEDBACK',
  CAMPAIGN_ASSIGNED = 'CAMPAIGN_ASSIGNED',
  CAMPAIGN_UPDATED = 'CAMPAIGN_UPDATED',
  DEADLINE_REMINDER = 'DEADLINE_REMINDER',
  CREATOR_STATUS = 'CREATOR_STATUS',
  // creator-management Phase 6: admin → creator direct message (outside a submission)
  CREATOR_DIRECT_MESSAGE = 'CREATOR_DIRECT_MESSAGE',
  SYSTEM = 'SYSTEM',
  PIPELINE_FAILED = 'PIPELINE_FAILED',
  // concept-approval-flow Phase 7: a concept entered your queue, or your
  // concept changed state. Must stay in sync with the Prisma enum and
  // notifications/schema.ts's Zod filter — see notification-type-parity.test.ts.
  CONCEPT_STATUS = 'CONCEPT_STATUS',
  // email-templatization: transactional (no in-app Notification row) email
  // types, previously inline HTML in their services. Must stay in sync with
  // the Prisma enum and notifications/schema.ts's Zod filter — see
  // notification-type-parity.test.ts.
  WELCOME_SET_PASSWORD = 'WELCOME_SET_PASSWORD',
  PASSWORD_RESET = 'PASSWORD_RESET',
  PARTNERSHIP_INVITE = 'PARTNERSHIP_INVITE',
  HOME_MAGIC_LINK = 'HOME_MAGIC_LINK',
}

// Must stay in sync with the Prisma EmailDeliveryStatus enum. SKIPPED = the
// transport intentionally did not send (EMAIL_SENDING_ENABLED=false global
// kill switch, or dev-noop mode with no SMTP creds) — never a false SENT.
export enum EmailDeliveryStatus {
  PENDING = 'PENDING',
  SENT = 'SENT',
  FAILED = 'FAILED',
  SKIPPED = 'SKIPPED',
}

// ─── Creator Approval Enums ─────────────────────────────

export enum CreatorShortlistStatus {
  PENDING = 'PENDING',
  APPROVED = 'APPROVED',
  // Admin-designated: client-approved but held in reserve (not live on campaign).
  // Distinct from APPROVED so analytics counts are intentional.
  BACKUP = 'BACKUP',
  REJECTED = 'REJECTED',
  CHANGES_REQUESTED = 'CHANGES_REQUESTED',
}

export enum CreatorDecisionType {
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum CreatorRejectionReason {
  BRAND_FIT = 'BRAND_FIT',
  AUDIENCE_MISMATCH = 'AUDIENCE_MISMATCH',
  CONTENT_STYLE = 'CONTENT_STYLE',
  PERFORMANCE_RISK = 'PERFORMANCE_RISK',
  COMPLIANCE_RISK = 'COMPLIANCE_RISK',
  OTHER = 'OTHER',
}

// ─── Posts Tracker Enums & Constants ────────────────────
// HERO is intentionally NOT a value here — Hero is a separate boolean
// flag on CreatorPost so a post can be "Relevant + Hero" simultaneously.
export enum PostTagStatus {
  RELEVANT = 'RELEVANT',
  NOT_RELEVANT = 'NOT_RELEVANT',
  NEEDS_REVIEW = 'NEEDS_REVIEW',
}

// Alias for ergonomic consumer access
export const POST_TAG_STATUS = PostTagStatus;

export const POST_TAG_STATUS_LABELS: Record<PostTagStatus, string> = {
  [PostTagStatus.RELEVANT]: 'Relevant',
  [PostTagStatus.NOT_RELEVANT]: 'Not Relevant',
  [PostTagStatus.NEEDS_REVIEW]: 'Needs Review',
};

export const POST_TAG_STATUS_COLORS: Record<PostTagStatus, string> = {
  [PostTagStatus.RELEVANT]: '#10B981',
  [PostTagStatus.NOT_RELEVANT]: '#9CA3AF',
  [PostTagStatus.NEEDS_REVIEW]: '#F59E0B',
};

// Hero flag visuals (orthogonal to PostTagStatus).
export const HERO_COLOR = '#8B5CF6';
export const HERO_LABEL = 'Hero';
export const HERO_ICON = '★';

// ─── Post content type (filter taxonomy) ─────────────────────────────────────
// Maps to Prisma enum `PostContentType`. Lets admins filter posts by format
// (Reel vs Post vs Carousel etc.) independently of platform or relevance.
export enum PostContentType {
  IG_POST = 'IG_POST',
  IG_REEL = 'IG_REEL',
  IG_CAROUSEL = 'IG_CAROUSEL',
  IG_STORY = 'IG_STORY',
  TT_VIDEO = 'TT_VIDEO',
  YT_VIDEO = 'YT_VIDEO',
  YT_SHORT = 'YT_SHORT',
}

export const POST_CONTENT_TYPE_LABELS: Record<PostContentType, string> = {
  [PostContentType.IG_POST]: 'Instagram Post',
  [PostContentType.IG_REEL]: 'Instagram Reel',
  [PostContentType.IG_CAROUSEL]: 'Instagram Carousel',
  [PostContentType.IG_STORY]: 'Instagram Story',
  [PostContentType.TT_VIDEO]: 'TikTok Video',
  [PostContentType.YT_VIDEO]: 'YouTube Video',
  [PostContentType.YT_SHORT]: 'YouTube Short',
};

// Display labels for submission `ContentType` (the format a creator uploads).
// Shared so product UI and help-guide specs read the same wording.
export const CONTENT_TYPE_LABELS: Record<ContentType, string> = {
  [ContentType.REEL]: 'Reel',
  [ContentType.STORY]: 'Story',
  [ContentType.POST]: 'Post',
  [ContentType.TIKTOK]: 'TikTok',
  [ContentType.YOUTUBE]: 'YouTube',
};
